import { mergeConfig } from '@kubb/plugin-client/clients/fetch';
import type { Client, RequestConfig } from '@kubb/plugin-client/clients/fetch';

type PreInterceptor = (url: string, init: RequestInit) => Promise<{ url: string; init: RequestInit }>;
type PostInterceptor = (response: Response) => Promise<Response>;

export interface FetchClientConfig {
  baseURL: string;
  pre?: PreInterceptor[];
  post?: PostInterceptor[];
}

export function createFetchClient(config: FetchClientConfig): Client {
  return async (requestConfig: RequestConfig) => {
    const merged = mergeConfig<RequestConfig>({ baseURL: config.baseURL } as RequestConfig, requestConfig);

    const normalizedParams = new URLSearchParams();
    Object.entries(merged.params || {}).forEach(([key, value]) => {
      if (value !== undefined) normalizedParams.append(key, value === null ? 'null' : String(value));
    });
    let url = [merged.baseURL, merged.url].filter(Boolean).join('');
    if (merged.params) url += `?${normalizedParams}`;

    let init: RequestInit = {
      method: merged.method?.toUpperCase() ?? 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(Array.isArray(merged.headers) ? Object.fromEntries(merged.headers) : (merged.headers ?? {})),
      },
      body: merged.data instanceof FormData ? merged.data : merged.data != null ? JSON.stringify(merged.data) : undefined,
      credentials: merged.credentials ?? 'include',
      signal: merged.signal,
    };

    for (const interceptor of config.pre ?? []) {
      ({ url, init } = await interceptor(url, init));
    }

    let response = await fetch(url, init);

    for (const interceptor of config.post ?? []) {
      response = await interceptor(response.clone());
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw Object.assign(new Error(`API error: ${response.status} ${response.statusText}`), {
        response,
        data,
      });
    }

    const data = [204, 205, 304].includes(response.status) || !response.body ? {} : await response.json();

    return { data, status: response.status, statusText: response.statusText, headers: response.headers };
  };
}
